// Anita Week 4 / Day 1 — Map and Simple Grid. U.S. Grade-3 math floor with a
// print-ready 10×10 coordinate grid, route table, park perimeter and a real
// logic prompt. Content follows the FINAL P0 source-of-truth package.
// SAFETY: only the fictional Nimny learning map is used.

const day1 = {
  id: 'day-1',
  studentId: 'anita',
  weekId: 'week-4',
  dayId: 'day-1',
  status: 'active',
  title: 'Карта и простая сетка / Map and Simple Grid',
  shortTitle: 'Карта и сетка / Map and Grid',
  theme:
    'Анита учится читать простую карту, считать клетки маршрута и понимать масштаб. / Anita learns to read a simple map, count route cells and understand scale.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'map', 'grid', 'logic', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни расстелила на столе большую карту в клетку. Каждая клетка — это кусочек настоящего пути. Сегодня Анита учится считать клетки, находить точки и понимать масштаб. / Nimny opens a big grid map. Every cell is a piece of a real path. Today Anita learns to count cells, find points and understand scale.',
  goals: [
    'Читать простую карту в клетку. / Read a simple grid map.',
    'Считать длину маршрута по клеткам и переводить в метры. / Count route cells and convert to meters.',
    'Находить и записывать координаты точки. / Find and write point coordinates.',
    'Считать периметр прямоугольника. / Find the perimeter of a rectangle.',
    'Найти логическую ошибку в рассуждении Нимни. / Spot a logic mistake.',
    'Написать простые русские слова карты. / Write simple Russian map words.'
  ],
  vocabulary: [
    { term: 'карта / map', definition: 'рисунок места сверху. / a drawing of a place from above.' },
    { term: 'сетка / grid', definition: 'клетки на карте. / cells on a map.' },
    { term: 'клетка / cell', definition: 'один маленький квадрат сетки. / one small grid square.' },
    { term: 'старт / start', definition: 'место, откуда начинается путь. / where the path begins.' },
    { term: 'масштаб / scale', definition: 'сколько метров в одной клетке. / how many meters in one cell.' },
    { term: 'координата / coordinate', definition: 'два числа: вправо и вверх. / two numbers: right and up.' },
    { term: 'периметр / perimeter', definition: 'длина вокруг фигуры. / length around a shape.' },
    { term: 'маршрут / route', definition: 'путь от старта до цели. / path from start to goal.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Задание 1 — Слова карты / Map Words',
      prompt: 'Прочитай по-русски:\nкарта\nсетка\nклетка\nстарт\nмасштаб\nкоордината\nпериметр\nмаршрут',
      ruPrimaryText: 'Прочитай 8 русских слов карты.',
      enHelpText: 'Read 8 map words in Russian first. English is only help.',
      hint: 'Читай по слогам; родитель может помочь. / Read by syllables; a parent may help.',
      answer: 'Анита пробует читать русские слова. / Anita attempts the Russian reading.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Задание 2 — Маленькое чтение / Short Reading',
      prompt:
        'Прочитай по-русски:\nКарта помогает увидеть путь сверху.\nНа карте можно считать клетки и выбирать маршрут.\nЕсли клетка имеет масштаб, мы узнаём настоящее расстояние.\nХороший маршрут не всегда самый прямой.\nИногда нужно обойти воду, дорогу или забор.',
      ruPrimaryText: 'Прочитай 5 коротких фраз про карту, клетки и маршрут.',
      enHelpText: 'A map shows the path from above. Cells help you count. Scale tells the real distance. A good route is not always the straightest one. Sometimes you go around water, road or a fence.',
      answer: 'Анита пробует читать 5 фраз. / Anita attempts the reading.'
    },
    {
      id: 'math-route-cells',
      type: 'text',
      title: 'Задание 3 — Маршрут по клеткам / Route by Cells',
      prompt:
        'На карте Анита идёт от точки S: 6 клеток вправо и 4 клетки вверх.\nОдна клетка = 5 метров.\n\nОтветь:\n1. Сколько клеток всего?\n2. Сколько метров весь маршрут?',
      ruPrimaryText: 'Реши двухшаговую задачу: сложи клетки, потом умножь на масштаб 5 м.',
      enHelpText: 'Add the grid steps first, then multiply by 5.',
      answer: '1) 6 + 4 = 10 клеток. 2) 10 × 5 = 50 метров.'
    },
    {
      id: 'math-two-routes',
      type: 'text',
      title: 'Задание 4 — Два маршрута / Two Routes',
      prompt:
        'Маршрут A: 5 клеток вправо и 3 клетки вверх.\nМаршрут B: 4 клетки вправо и 5 клеток вверх.\nОдна клетка = 10 метров.\n\nОтветь:\n1. Сколько метров маршрут A?\n2. Сколько метров маршрут B?\n3. Какой маршрут короче и на сколько метров?',
      ruPrimaryText: 'Сравни два маршрута через масштаб 10 м на клетку.',
      enHelpText: 'Find the steps in each route, then compare.',
      answer: '1) A: 5 + 3 = 8; 8 × 10 = 80 м. 2) B: 4 + 5 = 9; 9 × 10 = 90 м. 3) A короче на 90 − 80 = 10 м.'
    },
    {
      id: 'math-coordinates',
      type: 'text',
      title: 'Задание 5 — Координаты / Coordinates',
      prompt:
        'Нимни стоит в точке (2, 3).\nОна идёт на 4 клетки вправо и на 2 клетки вверх.\n\nОтветь:\n1. Какое новое x?\n2. Какое новое y?\n3. В какой точке (x, y) она окажется?',
      ruPrimaryText: 'Найди новую координату после движения от (2, 3).',
      enHelpText: 'Right changes the first number. Up changes the second.',
      answer: '1) x: 2 + 4 = 6. 2) y: 3 + 2 = 5. 3) Новая точка: (6, 5).'
    },
    {
      id: 'math-park-perimeter',
      type: 'text',
      title: 'Задание 6 — Периметр парка / Park Perimeter',
      prompt:
        'Парк на карте имеет форму прямоугольника.\nДлина 14 метров, ширина 8 метров.\n\nОтветь:\n1. Сложи все 4 стороны.\n2. Проверь через формулу 2 × (длина + ширина).\n3. Чему равен периметр?',
      ruPrimaryText: 'Найди периметр прямоугольного парка 14 × 8.',
      enHelpText: 'Add all four sides.',
      answer: '1) 14 + 8 + 14 + 8 = 44. 2) 2 × (14 + 8) = 2 × 22 = 44. 3) 44 метра.'
    },
    {
      id: 'logic-only-right',
      type: 'text',
      title: 'Задание 7 — Логика: только вправо / Only Right?',
      prompt:
        'Нимни сказала: «Если идти только вправо, всегда попадёшь к цели.»\n\nНайди ошибку.\nКогда нужно идти не только вправо, но и вверх или вниз? Ответь 1–3 предложениями.',
      ruPrimaryText: 'Найди ошибку в правиле «только вправо» и объясни.',
      enHelpText: 'Right alone rarely reaches a target that is above or below.',
      answer: 'Open-ended. Accept: «Если цель выше или ниже, надо идти вверх/вниз, иначе никогда не дойдёшь.»'
    },
    {
      id: 'spelling-day1',
      type: 'text',
      title: 'Задание 8 — Правописание / Spelling',
      prompt:
        'Напиши:\nкарта\nпуть\nклетка\nвверх\nпарк\n\nВставь буквы:\nка_та\nпу_ь\nкле_ка\nв_ерх\nпа_к',
      ruPrimaryText: 'Напиши 5 слов карты и вставь пропущенные буквы.',
      enHelpText: 'Write 5 map words and fill in the missing letters.',
      answer: 'карта, путь, клетка, вверх, парк.'
    },
    {
      id: 'creative-map-day1',
      type: 'text',
      title: 'Задание 9 — Моя маленькая карта / My Small Map',
      prompt:
        'Нарисуй маленькую карту с сеткой 6×6.\nПоставь точку S внизу слева.\nНарисуй путь: 3 клетки вправо и 2 клетки вверх.\nПодпиши: «1 клетка = 5 м».\n\nНапиши: путь = ___ клеток = ___ метров.',
      ruPrimaryText: 'Нарисуй сетку 6×6, старт S, путь 3 вправо + 2 вверх, масштаб 5 м.',
      enHelpText: 'Draw a 6×6 grid, start at S, path 3 right + 2 up, scale 5 m per cell.',
      hint: 'Карта учебная и выдуманная. / Learning map only.',
      answer: 'Open-ended. Ожидаемо: 3 + 2 = 5 клеток; 5 × 5 = 25 м.'
    },
    {
      id: 'speech-day1',
      type: 'text',
      title: 'Задание 10 — Объясни родителю / Explain to Parent',
      prompt:
        'Скажи родителю по-русски:\nЭто карта.\nЗдесь клетки.\nЯ иду от старта.\nОдна клетка — это 5 метров.',
      ruPrimaryText: 'Объясни родителю карту, клетки, старт и масштаб по-русски.',
      enHelpText: 'Tell the parent: this is a map, these are cells, I start from S, one cell is 5 meters.',
      answer: 'Accept if Anita explains map/cells/start/scale in Russian with support.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Начерти сетку 10 × 10 (клетки любого размера).',
    '2. Поставь S внизу-слева. Подпиши оси x и y.',
    '3. Реши задачи 3–6.',
    '4. Нарисуй свою маленькую карту 6×6.',
    '5. Расскажи родителю про карту и клетки.'
  ],
  printWorksheet: {
    title: 'Карта и простая сетка / Map and Simple Grid',
    subtitle: 'Неделя 4 · День 1 / Week 4 · Day 1',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Имя: __________________________   Дата: __________________________' },
          { type: 'text', text: 'Миссия: Нимни расстелила карту в клетку. Сегодня ты считаешь клетки, находишь точки и понимаешь масштаб. / Nimny opens a grid map. Today you count cells, find points and understand scale.' },
          { type: 'text', text: 'Задание 1 — Слова карты. Прочитай и обведи:' },
          { type: 'list', items: [
            'карта / map',
            'сетка / grid',
            'клетка / cell',
            'старт / start',
            'масштаб / scale',
            'координата / coordinate',
            'периметр / perimeter',
            'маршрут / route'
          ] },
          { type: 'text', text: 'Задание 2 — Маленькое чтение. Поставь галочку после чтения:' },
          { type: 'list', items: [
            '□ Карта помогает увидеть путь сверху.',
            '□ На карте можно считать клетки и выбирать маршрут.',
            '□ Если клетка имеет масштаб, мы узнаём настоящее расстояние.',
            '□ Хороший маршрут не всегда самый прямой.',
            '□ Иногда нужно обойти воду, дорогу или забор.'
          ] },
          { type: 'text', text: 'Задание 3 — Маршрут по клеткам. Готовая сетка 10 × 10. 1 клетка = 5 м. S — старт внизу слева.' },
          { type: 'list', items: [
            'y',
            '10 | □ □ □ □ □ □ □ □ □ □',
            ' 9 | □ □ □ □ □ □ □ □ □ □',
            ' 8 | □ □ □ □ □ □ □ □ □ □',
            ' 7 | □ □ □ □ □ □ □ □ □ □',
            ' 6 | □ □ □ □ □ □ □ □ □ □',
            ' 5 | □ □ □ □ □ □ □ □ □ □',
            ' 4 | □ □ □ □ □ □ □ □ □ □',
            ' 3 | □ □ □ □ □ □ □ □ □ □',
            ' 2 | □ □ □ □ □ □ □ □ □ □',
            ' 1 | S □ □ □ □ □ □ □ □ □',
            '     1 2 3 4 5 6 7 8 9 10  x',
            'Нарисуй путь: 6 клеток вправо и 4 клетки вверх.',
            'Клеток вправо: ___',
            'Клеток вверх: ___',
            'Клеток всего: ___',
            'Метров всего: ___'
          ] },
          { type: 'text', text: 'Задание 4 — Два маршрута. 1 клетка = 10 м. Заполни таблицу:' },
          { type: 'list', items: [
            'Маршрут | Вправо | Вверх | Клеток всего | Метров',
            '   A    |   5    |   3   |     ___      |  ___',
            '   B    |   4    |   5   |     ___      |  ___',
            'Какой короче: ___',
            'На сколько метров: ___'
          ] }
        ]
      },
      {
        title: 'Координаты и периметр / Coordinates and Perimeter',
        blocks: [
          { type: 'text', text: 'Задание 5 — Координаты. Готовая координатная сетка 8×8. Поставь точку S = (2,3). Двигайся 4 клетки вправо и 2 клетки вверх.' },
          { type: 'list', items: [
            'y',
            '8 | □ □ □ □ □ □ □ □',
            '7 | □ □ □ □ □ □ □ □',
            '6 | □ □ □ □ □ □ □ □',
            '5 | □ □ □ □ □ □ □ □',
            '4 | □ □ □ □ □ □ □ □',
            '3 | □ S □ □ □ □ □ □',
            '2 | □ □ □ □ □ □ □ □',
            '1 | □ □ □ □ □ □ □ □',
            '    1 2 3 4 5 6 7 8  x',
            'Новое x: 2 + 4 = ___',
            'Новое y: 3 + 2 = ___',
            'Новая точка: (___, ___)',
            'Отметь новую точку на сетке.'
          ] },
          { type: 'text', text: 'Задание 6 — Периметр парка. Прямоугольный парк 14 × 8 метров.' },
          { type: 'list', items: [
            'верх = 14 м',
            'низ  = 14 м',
            'лево = 8 м',
            'право = 8 м',
            'Периметр = ___ + ___ + ___ + ___ = ___ м',
            'Проверь: 2 × (14 + 8) = 2 × ___ = ___ м'
          ] },
          { type: 'text', text: 'Задание 7 — Логика: только вправо. Нимни сказала: «Если идти только вправо, всегда попадёшь к цели.» Найди ошибку и напиши 1–3 предложения.' },
          { type: 'text', text: 'Ответ: __________________________________________________________________________________________________________________' }
        ]
      },
      {
        title: 'Русский и проект / Russian and Project',
        blocks: [
          { type: 'text', text: 'Задание 8 — Правописание. Напиши и вставь буквы:' },
          { type: 'list', items: [
            'карта   путь   клетка   вверх   парк',
            'ка_та     пу_ь     кле_ка     в_ерх     па_к'
          ] },
          { type: 'text', text: 'Задание 9 — Моя маленькая карта. Готовая сетка 6×6. Поставь S внизу слева. Нарисуй путь: 3 клетки вправо и 2 клетки вверх. Подпиши «1 клетка = 5 м».' },
          { type: 'list', items: [
            '6 | □ □ □ □ □ □',
            '5 | □ □ □ □ □ □',
            '4 | □ □ □ □ □ □',
            '3 | □ □ □ □ □ □',
            '2 | □ □ □ □ □ □',
            '1 | S □ □ □ □ □',
            '    1 2 3 4 5 6',
            'Путь = ___ клеток = ___ метров.'
          ] },
          { type: 'text', text: 'Задание 10 — Объясни родителю. Скажи родителю:' },
          { type: 'list', items: [
            '□ Это карта.',
            '□ Здесь клетки.',
            '□ Я иду от старта.',
            '□ Одна клетка = 5 метров.'
          ] },
          { type: 'text', text: 'Parent Check: □ прочитала слова  □ прочитала 5 фраз  □ маршрут по клеткам  □ координаты  □ периметр парка  □ логика  □ карта 6×6  □ объяснила родителю.' },
          { type: 'text', text: 'Notebook Mode fallback: если нет принтера, начерти сетки 10×10, 8×8 и 6×6 в тетради в клетку и повтори все задания.' }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита читает сетку карты, считает маршрут по клеткам, находит координаты и периметр.',
    mathGoal: 'U.S. Grade 3: двухшаговые задачи (сложение + умножение), координаты (x, y), периметр прямоугольника, масштаб 1 клетка = N м.',
    russianGoal: 'Прочитать 8 слов карты и 5 фраз; написать 5 слов и вставить буквы.',
    readingWritingGoal: 'Ответ 1–3 предложениями на логический вопрос.',
    practicalGoal: 'Нарисовать простую карту с сеткой 6×6, стартом и масштабом.',
    parentTheoryCard: {
      title: 'Как объяснить карту, сетку и масштаб',
      purpose: 'Помочь родителю провести Аниту от абстрактной клетки к реальным метрам.',
      keyMessage: 'Каждая клетка — это не просто квадратик, это кусочек настоящего пути.',
      parentScript: [
        '«Смотри: одна клетка = 5 метров. Если ты прошла 6 клеток, это 6 × 5 = 30 метров.»',
        '«Координата — это два числа: сначала вправо, потом вверх.»',
        '«Периметр — это длина вокруг фигуры. Считаем все 4 стороны.»',
        '«Маршрут не всегда самый короткий. Смотри, где вода или забор.»'
      ],
      practiceSteps: [
        'Покажи сетку 10 × 10 в задании 3.',
        'Проведи пальцем 6 вправо, потом 4 вверх, и сосчитай клетки вслух.',
        'Умножь клетки на масштаб.',
        'В задании 5 покажи точку S и посчитай новую координату.',
        'В задании 6 посчитайте периметр вместе двумя способами.'
      ],
      doNotSay: [
        'Не решай задачу за Аниту.',
        'Не требуй идеального рисунка карты.',
        'Не пропускай проверку «периметр = сумма всех сторон».'
      ],
      paperOnlyNotes: [
        'Можно чертить сетку в тетради в клетку.',
        'Главное — старт, направление и масштаб.'
      ]
    },
    whatThisLessonTeaches: 'Анита учится связывать клетки на бумаге с настоящими метрами и находить точки по координатам.',
    keyMessage: 'Карта — это не картинка, а инструмент: клетки, координаты и масштаб дают числа.',
    howToExplain: 'Скажите: «Клетка — это единица. Умножь клетки на масштаб — получишь метры. Координата — вправо, потом вверх.»',
    exampleDialogue:
      'Parent: Сколько клеток от S?\nChild: 10.\nParent: Одна клетка — 5 метров. Сколько всего?\nChild: 50.\nParent: А координата (2,3) — куда двигаться?\nChild: 4 вправо, 2 вверх.\nParent: Новая точка?\nChild: (6, 5).',
    readingSupport: 'Слова «координата», «периметр», «масштаб» можно читать по частям.',
    russianSpeechGoal: 'Анита говорит: «Это карта. Здесь клетки. Я иду от старта. Одна клетка = 5 метров.»',
    mathAnswerKey:
      'math-route-cells: 1) 6 + 4 = 10 клеток. 2) 10 × 5 = 50 метров.\n' +
      'math-two-routes: 1) A: 5 + 3 = 8; 8 × 10 = 80 м. 2) B: 4 + 5 = 9; 9 × 10 = 90 м. 3) A короче на 90 − 80 = 10 м.\n' +
      'math-coordinates: 1) x: 2 + 4 = 6. 2) y: 3 + 2 = 5. 3) Новая точка: (6, 5).\n' +
      'math-park-perimeter: 14 + 8 + 14 + 8 = 44; проверка 2 × (14 + 8) = 44 метра.',
    spellingAnswerKey: 'карта\nпуть\nклетка\nвверх\nпарк',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка русского чтения 8 слов.' },
      { taskId: 'reading-sentences', answer: 'Попытка русского чтения 5 фраз.' },
      { taskId: 'math-route-cells', answer: '10 клеток; 50 метров.' },
      { taskId: 'math-two-routes', answer: 'A = 80 м; B = 90 м; A короче на 10 м.' },
      { taskId: 'math-coordinates', answer: 'x = 6; y = 5; точка (6, 5).' },
      { taskId: 'math-park-perimeter', answer: '44 метра.' },
      { taskId: 'logic-only-right', answer: 'Ошибка: цель может быть выше/ниже. Нужно и вверх, и вниз.' },
      { taskId: 'spelling-day1', answer: 'карта, путь, клетка, вверх, парк.' },
      { taskId: 'creative-map-day1', answer: 'Карта 6×6 с S, путь 3+2 = 5 клеток = 25 м.' },
      { taskId: 'speech-day1', answer: 'Объяснение карты/клеток/старта/масштаба по-русски.' }
    ],
    check: [
      'Анита прочитала слова и фразы.',
      'Анита посчитала маршрут по клеткам.',
      'Анита нашла новую координату.',
      'Анита посчитала периметр парка.',
      'Анита объяснила логическую ошибку.',
      'Анита нарисовала маленькую карту.',
      'Анита объяснила родителю карту, клетки и масштаб.'
    ],
    spellingCheck: 'Проверить карта, путь, клетка, вверх, парк.',
    readingCheck: 'Главное — попытка русского чтения и понимание смысла.',
    practicalCheck: 'Карта может быть детской. Важно: старт, направление и масштаб.',
    drawingCriteria: 'Есть сетка, старт S, путь 3 + 2 клетки и подпись масштаба.',
    commonMistakes: [
      '«Клетка = 1 метр всегда.» Правильно: «Клетка = столько метров, сколько написано в масштабе.»',
      '«Координата (2,3) — это две отдельные точки.» Правильно: «Это одна точка: 2 вправо, 3 вверх.»',
      '«Периметр — только две стороны.» Правильно: «Периметр — все четыре стороны.»'
    ],
    discussionPrompts: [
      'Что важнее в маршруте: длина или удобство?',
      'Как узнать настоящий путь, если карта маленькая?',
      'Что легче найти на карте — точку или маршрут?'
    ],
    ifEasy: 'Попросить Аниту сделать 3-й маршрут C: 7 клеток вправо и 2 вверх, и сравнить со всеми.',
    ifDifficult: 'Оставить только задания 3, 5 и 6; координата (2,3) может быть заменена на (1,1).',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за аккуратную карту и точную координату.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Не требуй идеальной сетки. Цель — клетки, направление, масштаб.',
    safetyNotes: 'Карта учебная и выдуманная. Не рисуйте настоящие адреса или маршруты — реальные семейные маршруты только на бумаге с родителем.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'neat-grid-map', label: 'Бонус за аккуратную карту 6×6', amount: 1 },
      { id: 'clear-coordinate', label: 'Бонус за точную координату', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'math-route-cells',
      'math-two-routes',
      'math-coordinates',
      'math-park-perimeter',
      'logic-only-right',
      'spelling-day1',
      'creative-map-day1',
      'speech-day1'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-4-day-2'
};

export default day1;
